import { LABELS } from "~/resources/Labels";
import { PropRow } from "~/models/Commons ";

const DATE_TIME_REGEX =
  /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/;

const parseDateTime = (
  value: unknown
): number | null => {
  if (typeof value !== "string") {
    return null;
  }

  const match = value.trim().match(DATE_TIME_REGEX);

  if (!match) {
    return null;
  }

  const [
    ,
    day,
    month,
    year,
    hours = "00",
    minutes = "00",
    seconds = "00",
  ] = match;

  const date = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hours),
    Number(minutes),
    Number(seconds)
  );

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.getTime();
};

const isNumber = (value: unknown): boolean => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return false;
  }

  return !Number.isNaN(Number(value));
};

const compareValues = (
  firstValue: unknown,
  secondValue: unknown
): number => {
  const firstDate = parseDateTime(firstValue);
  const secondDate = parseDateTime(secondValue);

  // Date comparison
  if (
    firstDate !== null &&
    secondDate !== null
  ) {
    return firstDate - secondDate;
  }

  // Keep dates before non-date values
  if (
    firstDate !== null &&
    secondDate === null
  ) {
    return -1;
  }

  if (
    firstDate === null &&
    secondDate !== null
  ) {
    return 1;
  }

  // Number comparison
  if (
    isNumber(firstValue) &&
    isNumber(secondValue)
  ) {
    return (
      Number(firstValue) -
      Number(secondValue)
    );
  }

  // String comparison
  return String(firstValue ?? "").localeCompare(
    String(secondValue ?? ""),
    undefined,
    {
      numeric: true,
      sensitivity: "base",
    }
  );
};

export const sortTableData = (
  rows: PropRow[],
  sortConfig: {
    key: string;
    direction: string;
  },
  defaultSortKey?: string
): PropRow[] => {
  const activeKey = sortConfig.key || defaultSortKey || "";

  if (!activeKey) {
    return [...rows];
  }

  // When no column is clicked, always sort descending (newest first for dates).
  const activeDirection = sortConfig.key
    ? sortConfig.direction
    : LABELS.Desc;

  return [...rows].sort(
    (firstRow, secondRow) => {
      // Prefer the raw ISO value (_raw sibling field) for date columns.
      // ISO strings are year-first so they sort correctly as plain text.
      const rawKey = activeKey + "_raw";
      const firstRaw = firstRow.row?.[rawKey];
      const secondRaw = secondRow.row?.[rawKey];

      if (firstRaw !== undefined || secondRaw !== undefined) {
        const result = String(firstRaw ?? "").localeCompare(
          String(secondRaw ?? ""),
          undefined,
          { numeric: false, sensitivity: "base" }
        );
        return activeDirection === LABELS.Desc ? -result : result;
      }

      const firstValue = firstRow.row?.[activeKey];
      const secondValue = secondRow.row?.[activeKey];

      const result = compareValues(
        firstValue,
        secondValue
      );

      return activeDirection === LABELS.Desc
        ? -result
        : result;
    }
  );
};